import { inject, NgModule } from '@angular/core';
import { CanActivateFn, Router, RouterModule, Routes } from '@angular/router';
import { userManagementGuard } from '@luzula/luzula-lib';
import { AuthService } from './auth.service';
import { canAccessListenverwaltung, canAccessBenutzerverwaltung, canAccessDruckLayout, canAccessDatenbankverwaltung } from './role-model';
import { ConnectionGuard } from './connection-guard';
import { AlleBohrpunkteComponent } from './alle-bohrpunkte/alle-bohrpunkte.component';
import { ListenverwaltungComponent } from './listenverwaltung/listenverwaltung.component';
import { BodensteckbriefPreviewComponent } from './listenverwaltung/bodensteckbrief-preview.component';
import { BodensteckbriefEditorComponent } from './listenverwaltung/bodensteckbrief-editor.component';
import { BodenformenkatalogReaderComponent } from './listenverwaltung/bodenformenkatalog-reader.component';
import { UsersPageComponent } from './admin/users-page.component';
import { DbAdminPageComponent } from './admin/db-admin-page.component';
import { DruckLayoutComponent } from './druck-layout/druck-layout.component';

// Bohrpunkte-Komponenten für Routing (Barrel-Import)
import {
	BohrpunktComponent,
	BohrpunktPrintViewComponent,
	MultiPrintViewComponent,
	BohrpunktTabsComponent,
	bohrpunktDetailResolver,
	bohrpunkteDetailResolver,
	AdresseComponent,
	AufnahmeComponent,
	BodenComponent,
	NaturraumComponent,
	SubstrateHorizonteComponent,
	BaumartenComponent,
	BestandComponent,
	VegetationsaufnahmeComponent,
	KurzprotokollComponent,
	ErrorComponent,
	SandboxControlsComponent
} from './bohrpunkte';
import { KA_MASKEN_ROUTEN } from './bohrpunkte/verfahren/ka/ka-masken-routen';

const canDeactivateBp = (c: BohrpunktComponent) => c.canDeactivate();

/**
 * App-Verwaltungs-Guards (PERMISSIONS.md §8). Der synchrone Rollen-Bypass kommt
 * aus den Single-Source-Helfern in role-model.ts; der Admin-Pfad
 * (App-Verwaltung-Capability vom Keycloak-Client-Admin) wird asynchron über
 * `userManagementGuard` nachgereicht (daher `isAdmin = false` an die Helfer).
 *
 * - Listenverwaltung:   Admin ODER Entwickler ODER Manager
 * - Benutzerverwaltung: Admin ODER Entwickler (NICHT Manager)
 */
const listenverwaltungGuard: CanActivateFn = (route, state) => {
	const aus = inject(AuthService);
	if (canAccessListenverwaltung(false, r => aus.canIbe(r))) return true;
	return userManagementGuard(route, state);
};

const benutzerverwaltungGuard: CanActivateFn = (route, state) => {
	const aus = inject(AuthService);
	if (canAccessBenutzerverwaltung(false, r => aus.canIbe(r))) return true;
	return userManagementGuard(route, state);
};

/**
 * Druck-Layout-Designer (PERMISSIONS.md §8) — NUR Entwickler (2026-06-28).
 * Kein Admin-Capability-Pfad (Projekt-Feature, kein App-Management). Nicht-
 * Entwickler werden auf die Startseite umgeleitet (Direktzugriff per URL gesperrt).
 */
const druckLayoutGuard: CanActivateFn = () => {
	const aus = inject(AuthService);
	if (canAccessDruckLayout(r => aus.canIbe(r))) return true;
	return inject(Router).createUrlTree(['/']);
};

/**
 * Datenbankverwaltung (PERMISSIONS.md §8) — NUR Entwickler (2026-08-03). Wie der
 * Druck-Layout-Guard ohne Admin-Capability-Pfad (reines Entwickler-Werkzeug).
 * Nicht-Entwickler werden auf die Startseite umgeleitet (Direktzugriff per URL
 * gesperrt).
 */
const datenbankverwaltungGuard: CanActivateFn = () => {
	const aus = inject(AuthService);
	if (canAccessDatenbankverwaltung(r => aus.canIbe(r))) return true;
	return inject(Router).createUrlTree(['/']);
};

const routes: Routes = [
	{
		path: '',
		component: AlleBohrpunkteComponent,
		canActivate: [ConnectionGuard]
	},
	{
		path: 'listenverwaltung',
		component: ListenverwaltungComponent,
		// App-Verwaltung (Listen): Admin ODER Entwickler (Direktzugriff per URL
		// sonst ungeschuetzt). Benutzerverwaltung bleibt strikt admin.
		canActivate: [listenverwaltungGuard]
	},
	{
		// Read-only-Vorschau eines Bodensteckbriefs aus der Bodensteckbriefe-Liste.
		// :id ist die Steckbrief-ID (Format "<pdf>#<page>"). Encoding ueberlaesst
		// die Listenverwaltung dem Router (commands-Array, nicht navigateByUrl).
		path: 'listenverwaltung/steckbrief/:id',
		component: BodensteckbriefPreviewComponent,
		canActivate: [listenverwaltungGuard]
	},
	{
		// Editor fuer einen Bodensteckbrief (gleiche :id-Logik wie die Vorschau).
		path: 'listenverwaltung/steckbrief/:id/edit',
		component: BodensteckbriefEditorComponent,
		canActivate: [listenverwaltungGuard]
	},
	{
		// Druck-Layout-Designer (Vollbild). Projektkontext bezieht die Komponente
		// selbst (ProjectService). Zugang NUR Entwickler (PERMISSIONS.md §8) —
		// Direktzugriff per URL via druckLayoutGuard gesperrt.
		path: 'druck-layout',
		component: DruckLayoutComponent,
		canActivate: [druckLayoutGuard]
	},
	{
		// Erst beim Aufschlagen laden: Das Handbuch ist eine der groessten Dateien
		// der App (rund 0,5 MB Vorlage), wird aber selten geoeffnet. Fest
		// eingebunden lud es jedes Geraet beim Start mit.
		path: 'handbuch',
		loadComponent: () => import('./handbuch/handbuch.component').then(m => m.HandbuchComponent)
	},
	{
		// BpWeb-2.0-Update-Heft („Was ist neu?"). Schlanker In-App-Rahmen, der das
		// statische Asset (assets/handbuch-2.0/index.html) als <iframe> zeigt.
		// Bewusst IN der App (nicht target=_blank/neuer Tab): so ist Hin und Zurueck
		// reine App-Navigation – kein App-Neuladen, keine Zurueck-Schleife.
		path: 'handbuch-neu',
		loadComponent: () => import('./handbuch/handbuch-update.component').then(m => m.HandbuchUpdateComponent)
	},
	{
		// Read-only-Katalog-Reader. Bewusst PUBLIC (kein Guard) - der
		// Bodenformenkatalog gehoert zum Referenz-/Handbuch-Material und ist
		// fuer alle eingeloggten User nutzbar. Aufrufer (z.B. (i)-Icon an
		// HBF/FBF-Feldern) navigieren mit der Steckbrief-ID hierher; die
		// Komponente blaettert via replaceUrl, damit Browser-Back zum
		// Aufrufer zurueckkehrt.
		path: 'bodenformenkatalog',
		component: BodenformenkatalogReaderComponent
	},
	{
		path: 'bodenformenkatalog/:id',
		component: BodenformenkatalogReaderComponent
	},
	{
		// SEA-Suche: Wortlaut der SEA 95 (Teile A-D) auf dem Geraet durchsuchen.
		// PUBLIC wie Handbuch und Bodenformenkatalog (Nutzer-Entscheidung
		// 28.09.2026). Erst beim Aufschlagen laden: Suchbibliothek und Kern
		// gehoeren nicht in den Start.
		path: 'sea-suche',
		loadComponent: () => import('./sea-suche/sea-suche.component').then(m => m.SeaSucheComponent)
	},
	// Bohrpunkte-Routen (ehemals BohrpunkteRoutingModule)
	{
		path: 'bohrpunkte/:id',
		component: BohrpunktComponent,
		canDeactivate: [canDeactivateBp],
		resolve: {
			bp: bohrpunktDetailResolver,
			bps: bohrpunkteDetailResolver
		},
		children: [
			{ path: '', component: BohrpunktTabsComponent },
			{ path: 'Adresse', component: AdresseComponent },
			{ path: 'Aufnahme', component: AufnahmeComponent },
			{ path: 'Boden', component: BodenComponent },
			{ path: 'Naturraum', component: NaturraumComponent },
			{ path: 'Substrate-Horizonte', component: SubstrateHorizonteComponent },
			{ path: 'Baumarten', component: BaumartenComponent },
			{ path: 'Bestand', component: BestandComponent },
			{ path: 'Vegetationsaufnahme', component: VegetationsaufnahmeComponent },
			{ path: 'Kurzprotokoll', component: KurzprotokollComponent },
			// Eingabemasken der Aufnahme nach der Bodenkundlichen Kartieranleitung
			...KA_MASKEN_ROUTEN,
		],
	},
	{
		path: 'admin/users',
		component: UsersPageComponent,
		// Benutzerverwaltung: Admin ODER Entwickler (Manager bleibt draußen).
		canActivate: [benutzerverwaltungGuard]
	},
	{
		// Datenbankverwaltung (eigener Menüpunkt seit 2026-08-03) — NUR Entwickler.
		// Reines Entwickler-Werkzeug (CouchDB-Admin über den Gateway), bewusst
		// getrennt von der Benutzerverwaltung.
		path: 'admin/databases',
		component: DbAdminPageComponent,
		canActivate: [datenbankverwaltungGuard]
	},
	{ path: 'bohrpunkte/sandbox', component: SandboxControlsComponent },
	{ path: 'bohrpunkte/Drucken/multi', component: MultiPrintViewComponent },
	{
		path: 'bohrpunkte/Drucken/:id',
		component: BohrpunktPrintViewComponent,
		resolve: {
			bp: bohrpunktDetailResolver,
			bps: bohrpunkteDetailResolver
		}
	},
	{ path: 'bohrpunkte', component: ErrorComponent }
];

@NgModule({
	imports: [RouterModule.forRoot(routes)],
	exports: [RouterModule]
})
export class AppRoutingModule { }
